import React, { useState, useEffect } from 'react';
import { DemoAPI } from '../../services/demo-api';

interface DemoHelpProps {
  currentScene: number;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  sessionKey?: string;
}

export const DemoHelp: React.FC<DemoHelpProps> = ({
  currentScene,
  isExpanded,
  onToggleExpanded,
  sessionKey
}) => {
  const [helpContent, setHelpContent] = useState<{ title: string; content: string[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch help content from API when scene changes
  useEffect(() => {
    const fetchHelpContent = async () => {
      console.log('🔍 [DemoHelp] Fetching help content for scene:', currentScene, 'sessionKey:', sessionKey);
      
      if (!sessionKey) {
        console.log('🔍 [DemoHelp] No session key, using fallback content');
        // Fallback to hardcoded content if no session key
        setHelpContent(getFallbackHelpContent(currentScene));
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        console.log('🔍 [DemoHelp] Making API call to get scene help content');
        const data = await DemoAPI.getSceneHelpContent(sessionKey, currentScene);
        console.log('🔍 [DemoHelp] API response:', data);
        setHelpContent(data.help_content);
      } catch (err) {
        console.error('🔍 [DemoHelp] Failed to fetch help content:', err);
        setError('Failed to load help content');
        setHelpContent(getFallbackHelpContent(currentScene));
      } finally {
        setLoading(false);
      }
    };

    fetchHelpContent();
  }, [currentScene, sessionKey]);

  // Fallback help content for when API is not available
  const getFallbackHelpContent = (sceneId: number): { title: string; content: string[] } => {
    const sceneHelpContent: Record<number, { title: string; content: string[] }> = {
      0: {
        title: "Welcome to Krysalis: Introduction and Company Setup",
        content: [
          "This is your introduction to KRYSALIS INC - the business operating system that thinks like your business.",
          "I don't just track data - I model how your business behaves: how demand becomes revenue, how teams create value, and how decisions ripple through the system.",
          "Krysalis is a business operating system that models how your business behaves: how demand becomes revenue, how teams create value, and how decisions ripple through the system."
        ]
      },
      1: {
        title: "Creating the Living Model: Business Flow Explanation and Artifact Creation",
        content: [
          "In this scene, we'll explore how every business operates as a flow of value - from Demand to Supply to Delivery.",
          "Your Task: Observe the business flow diagram. You'll be prompted to interact with it to understand how value moves through your business.",
          "Krysalis builds these flows for you, allowing you to populate them with real business artifacts."
        ]
      },
      2: {
        title: "The Organization Layer: Organizational Assessment and Scoring",
        content: [
          "This scene focuses on how your organization functions internally. Krysalis assesses key dimensions like Vision, Leadership, Strategy, Guidance, and Human Resources.",
          "Your Task: Review the organizational assessment card. You may be asked to confirm a scan or provide input on specific dimensions.",
          "Understand how Krysalis evaluates business health and identifies areas for improvement."
        ]
      },
      3: {
        title: "The Business Value Layer: Business Value Measurement and Analysis",
        content: [
          "Every business generates three types of value: Customer Value, Business Value, and Stakeholder Value. In this scene, we'll measure these through your current model.",
          "Your Task: Analyze the value dashboard. You might be prompted to explore specific value opportunities.",
          "See how Krysalis demonstrates value measurement capabilities and identifies areas to increase impact."
        ]
      },
      4: {
        title: "The Financial Flow: Financial Integration and Forecasting",
        content: [
          "This scene demonstrates how Krysalis connects your accounting data and provides financial forecasting.",
          "Your Task: Observe the financial dashboard. You'll see how Revenue Artifacts track what customers buy and when it's recognized.",
          "Explore the integration with accounting systems and the predictive modeling capabilities."
        ]
      },
      5: {
        title: "The Simulation Engine: Scenario Modeling and Simulation",
        content: [
          "In this scene, you'll run scenario simulations to understand the impact of different business decisions.",
          "Your Task: Select and run two scenarios: 'Increase marketing efficiency by 10%' and 'Improve production throughput by 15%'.",
          "See how Krysalis's simulation engine helps you compare outcomes and make informed strategic choices."
        ]
      },
      6: {
        title: "Continuous Learning and Self-Diagnosis: Problem Detection and Correction",
        content: [
          "Krysalis continuously monitors your business state transitions. When a process stalls or variance increases, it flags it - just like a nervous system detecting pain.",
          "Your Task: Review the detected problems and proposed corrective workflows. You might be asked to explore a specific problem.",
          "Understand how Krysalis provides automated monitoring and improvement suggestions."
        ]
      },
      7: {
        title: "Integration and Intelligence: System Integration and Data Translation",
        content: [
          "Krysalis connects natively or via API to your business ecosystem, including accounting, CRM, and HR systems.",
          "Your Task: Explore the integration hub to see connected and disconnected systems. You might be prompted to initiate a new integration.",
          "See how Krysalis provides ecosystem connectivity and data translation capabilities."
        ]
      },
      8: {
        title: "Insight & Governance: Reporting and Governance Features",
        content: [
          "Beyond operations, Krysalis supports leadership governance by generating board reports, cap-table updates, investor dashboards, and grant documentation automatically.",
          "Your Task: Review the governance dashboard and available reports. You might be asked to generate a specific report.",
          "Understand how Krysalis provides automated reporting and governance capabilities."
        ]
      },
      9: {
        title: "Closing Vision: Vision Summary and Lead Capture",
        content: [
          "In this final scene, we'll summarize the value proposition of Krysalis. It's not just a dashboard - it's a business nervous system that models, measures, and improves how value flows across your enterprise.",
          "Your Task: Provide your contact information to receive a summary of your demo experience and next steps.",
          "When you can see your business as a system, you can control time-to-earnings - and that changes everything."
        ]
      }
    };

    return sceneHelpContent[sceneId] || sceneHelpContent[0];
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{
        backgroundColor: 'rgba(59, 130, 246, 0.05)',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        borderRadius: '8px',
        marginBottom: '12px',
        padding: '12px 16px',
        color: '#6b7280'
      }}>
        Loading help content...
      </div>
    );
  }

  // Show error state
  if (error || !helpContent) {
    return (
      <div style={{
        backgroundColor: 'rgba(239, 68, 68, 0.05)',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        borderRadius: '8px',
        marginBottom: '12px',
        padding: '12px 16px',
        color: '#ef4444'
      }}>
        {error || 'No help content available'}
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'rgba(59, 130, 246, 0.05)',
      border: '1px solid rgba(59, 130, 246, 0.2)',
      borderRadius: '8px',
      marginBottom: '12px',
      overflow: 'hidden',
      transition: 'all 0.3s ease'
    }}>
      {/* Help Header */}
      <div 
        style={{
          padding: '12px 16px',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'background-color 0.2s ease'
        }}
        onClick={onToggleExpanded}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ 
            fontSize: '16px',
            color: '#3b82f6'
          }}>
            💡
          </div>
          <div style={{ 
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#3b82f6'
          }}>
            {helpContent.title}
          </div>
        </div>
        <div style={{
          fontSize: '18px',
          color: '#3b82f6',
          transition: 'transform 0.3s ease',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
        }}>
          ▼
        </div>
      </div>

      {/* Help Content */}
      {isExpanded && (
        <div style={{
          padding: '16px',
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
          <div style={{ fontSize: '13px', lineHeight: '1.6', color: '#e5e7eb' }}>
            {helpContent.content.map((paragraph: string, index: number) => (
              <div key={index} style={{ marginBottom: '12px' }}>
                {paragraph}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoHelp;